"use client";

import { Label } from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useLocale } from "next-intl";
import { useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
  DrawerTitle,
  DrawerHeader,
  DrawerDescription,
} from "@/components/ui/drawer";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const languages = [
  { code: "pl", label: "Polski", flag: "🇵🇱" },
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "uk", label: "Українська", flag: "🇺🇦" },
];

export default function LanguageSwitcher({ isMobile }: { isMobile: boolean }) {
  const pathname = usePathname();
  const locale = useLocale();
  const t = useTranslations("staticComponents.LanguageSwitcher");

  const switchLanguage = (newLocale: string) => {
    const newPathname = pathname.replace(new RegExp(`^/${locale}(/|$)`), "/");
    return `/${newLocale}${newPathname}`;
  };

  return (
    <div>
      <Drawer>
        <DrawerTrigger asChild>
          <Button
            variant="ghost"
            className="absolute bottom-4 right-4 flex items-center gap-2 md:hidden"
          >
            {languages.find((lang) => lang.code === locale)?.flag}{" "}
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DrawerTrigger>
        <DrawerContent className="p-4">
          <DrawerHeader>
            <DrawerTitle>{t("Wybierz_jezyk")}</DrawerTitle>
            <DrawerDescription>{t("Jezyk_obowiazuje")}</DrawerDescription>
          </DrawerHeader>
          <div className="flex flex-col space-y-2">
            {languages.map((lang) => (
              <Link
                key={lang.code}
                href={switchLanguage(lang.code)}
                className="w-full"
              >
                <Button
                  variant="ghost"
                  className="flex w-full items-center justify-between"
                >
                  {lang.label}
                  {locale === lang.code && (
                    <Check className="text-green-white h-4 w-4" />
                  )}
                </Button>
              </Link>
            ))}
          </div>
        </DrawerContent>
      </Drawer>

      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className="ml-2 hidden h-6 items-center gap-1 px-2 md:flex"
          >
            {languages.find((lang) => lang.code === locale)?.flag}{" "}
            <ChevronDown className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="my-1 w-32 p-2">
          <div className="flex flex-col">
            {languages.map((lang) => (
              <Link
                key={lang.code}
                href={switchLanguage(lang.code)}
                className="w-full"
              >
                <Button
                  variant="ghost"
                  className="flex w-full items-center justify-between"
                >
                  {lang.label}
                  {locale === lang.code && (
                    <Check className="text-green-white h-4 w-4" />
                  )}
                </Button>
              </Link>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
